// The one definition of how a percentage becomes a stored ratio and back.
//
// Dividing by 100 leaks the binary representation error into whatever consumes
// the result: `21.99 / 100` is `0.21989999999999998`, and Firestore then holds
// a number the user never typed. Multiplying by 100 is no better in the other
// direction — `0.2199 * 100` is `21.990000000000002` — so a value stored
// cleanly renders with noise.
//
// The round trip hides it. `(21.99 / 100) * 100` is exactly `21.99`, so the
// form always looked right while the document was wrong, which is why this
// survived until someone read a raw submission.
//
// 15 significant figures is one below the ~15.95 a double actually carries, so
// it discards the representation error and nothing else. A percentage in this
// product never has more than a few decimal places, and 12.3456789 survives
// intact, so the limit is nowhere near anything real.
const SIGNIFICANT_DIGITS = 15

const withoutRepresentationError = (value: number): number =>
  Number(value.toPrecision(SIGNIFICANT_DIGITS))

// Nullish in, null out, so every caller shares one empty-value rule. `== null`
// rather than `=== null` because the two clients disagree about what empty
// looks like: the portal's NumericFormat reports a cleared field as
// `undefined`, mobile reports `null`. A `=== null` check would turn the
// portal's cleared field into NaN.
//
// The nullable return needs no cast at the zod transform in
// submissions/answers/schemas.ts, because SubmissionAnswerValue is itself
// nullable — answerValueSchema ends in `.nullable()`.
export const percentToRatio = (
  percent: number | null | undefined
): number | null =>
  percent == null ? null : withoutRepresentationError(percent / 100)

export const ratioToPercent = (
  ratio: number | null | undefined
): number | null =>
  ratio == null ? null : withoutRepresentationError(ratio * 100)
